import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Platform, Modal, Pressable } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { PageContainer, ContentContainer, BottomContainer, HeaderContainer, Text, Input, Button, Theme, Card } from '../../components/ui';
import { Ionicons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { useFeedbackForm } from '../../hooks/useFeedbackForm';
import { FeedbackService } from '../../services/feedbackService';
import { auth } from '../../config/firebase';
import { PrivateProfileService } from '../../services/privateProfileService';
import type { ProfileStackParamList } from '../../navigation/ProfileStackNavigator';

const SUPPORT_EMAIL = 'support@mentorist.app';
const CATEGORY_OPTIONS: { label: string; value: 'bug' | 'feature' | 'support' | 'content' | 'other' }[] = [
  { label: 'Bug', value: 'bug' },
  { label: 'Feature', value: 'feature' },
  { label: 'Support', value: 'support' },
  { label: 'Content', value: 'content' },
  { label: 'Other', value: 'other' },
];

/**
 * Check if email is an Apple proxy email (Hide My Email feature)
 * These emails are unreliable for support communication
 */
function isAppleProxyEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const lowerEmail = email.toLowerCase();
  return (
    lowerEmail.includes('@privaterelay.appleid.com') ||
    lowerEmail.includes('privaterelay@icloud.com')
  );
}

const ContactUsScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<RouteProp<ProfileStackParamList, 'ContactUs'>>();
  const { values, errors, isSubmitting, canSubmit, setField, submit } = useFeedbackForm();
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  // Pre-fill from route params
  useEffect(() => {
    if (route.params?.category) {
      setField('category', route.params.category);
    }
    if (route.params?.subject) {
      setField('subject', route.params.subject);
    }
    if (route.params?.message) {
      setField('message', route.params.message);
    }
  }, [route.params, setField]);

  // Pre-fill email (skip Apple proxy emails)
  useEffect(() => {
    (async () => {
      try {
        let prefill = await FeedbackService.getPrefilledEmail();
        
        // If no stored email, try to get from profile
        if (!prefill) {
          const uid = auth?.currentUser?.uid;
          if (uid) {
            const result = await PrivateProfileService.getProfile(uid);
            if (result.success && (result.data as any)?.email) {
              prefill = (result.data as any).email as string;
            }
          }
        }
        
        // Fallback to auth email
        if (!prefill && auth?.currentUser?.email) {
          prefill = auth.currentUser.email as string;
        }
        
        // Only pre-fill if it's not an Apple proxy email
        if (prefill && !isAppleProxyEmail(prefill)) {
          setField('email', prefill);
        }
      } catch {}
    })();
  }, [setField]);

  const handleCopyEmail = async () => {
    try {
      const nav: any = (globalThis as any)?.navigator;
      if (Platform.OS === 'web' && nav?.clipboard?.writeText) {
        await nav.clipboard.writeText(SUPPORT_EMAIL);
        Toast.show({ type: 'success', text1: 'Email copied' });
      } else {
        Toast.show({ type: 'error', text1: 'Copy not supported on this device' });
      }
    } catch {
      Toast.show({ type: 'error', text1: 'Failed to copy email' });
    }
  };

  const handleSubmit = async () => {
    const res = await submit();
    if (res.success) {
      Toast.show({ type: 'success', text1: 'Thanks! Your feedback was sent.' });
      setField('message', '');
    } else {
      Toast.show({ type: 'error', text1: res.error || 'Failed to send feedback' });
    }
  };

  return (
    <PageContainer>
      <View style={styles.header}>
        <Pressable 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={Theme.colors.textPrimary} />
        </Pressable>
        <Text variant="heading3" style={styles.headerTitle}>
          Contact Us
        </Text>
        <View style={styles.placeholder} />
      </View>

      <ContentContainer>

        <Card style={styles.card}>
          <Text variant="body2" color="secondary" style={styles.sectionLabel}>Feedback type (optional)</Text>
          <Pressable style={styles.dropdown} onPress={() => setIsCategoryOpen(true)}>
            <Text variant="body1" style={styles.dropdownText}>
              {values.category ? CATEGORY_OPTIONS.find(c => c.value === values.category)?.label : 'Select feedback type'}
            </Text>
          </Pressable>

          <Modal visible={isCategoryOpen} transparent animationType="fade" onRequestClose={() => setIsCategoryOpen(false)}>
            <Pressable style={styles.modalBackdrop} onPress={() => setIsCategoryOpen(false)}>
              <View />
            </Pressable>
            <View style={styles.modalCenter} pointerEvents="box-none">
              <Card style={styles.modalCard}>
                {CATEGORY_OPTIONS.map(opt => (
                  <Pressable
                    key={opt.value}
                    style={[styles.optionRow, values.category === opt.value && styles.optionRowSelected]}
                    onPress={() => { setField('category', opt.value); setIsCategoryOpen(false); }}
                  >
                    <Text variant="body1">{opt.label}</Text>
                  </Pressable>
                ))}
                <View style={styles.divider} />
                <Pressable style={styles.optionRow} onPress={() => { setField('category', undefined as any); setIsCategoryOpen(false); }}>
                  <Text variant="body1" color="secondary">Clear selection</Text>
                </Pressable>
                <Pressable style={styles.optionRow} onPress={() => setIsCategoryOpen(false)}>
                  <Text variant="body1" color="secondary">Cancel</Text>
                </Pressable>
              </Card>
            </View>
          </Modal>

          <View style={styles.divider} />

          <View>
            <Text variant="body2" color="secondary" style={styles.fieldLabel}>
              Your email {!values.email && <Text variant="body2" color="error">*</Text>}
            </Text>
            {!values.email && (
              <Text variant="caption" color="secondary" style={styles.emailHint}>
                Please provide a real email so we can respond to you
              </Text>
            )}
          </View>
          <Input
            value={values.email || ''}
            onChangeText={(t) => setField('email', t)}
            placeholder="your.email@example.com"
            keyboardType="email-address"
            autoCapitalize="none"
            error={Boolean(errors.email)}
            errorMessage={errors.email}
          />

          <View style={styles.gap} />

          <Text variant="body2" color="secondary" style={styles.fieldLabel}>
            Subject (optional)
          </Text>
          <Input
            value={values.subject || ''}
            onChangeText={(t) => setField('subject', t)}
            placeholder="Brief summary of your request"
          />

          <View style={styles.gap} />

          <Text variant="body2" color="secondary" style={styles.fieldLabel}>
            Message {!values.message && <Text variant="body2" color="error">*</Text>}
          </Text>
          <Input
            value={values.message}
            onChangeText={(t) => setField('message', t)}
            placeholder="Describe your issue or question in detail..."
            multiline
            numberOfLines={6}
            style={{ minHeight: 144 }}
            error={Boolean(errors.message)}
            errorMessage={errors.message}
          />
        </Card>

        <View style={styles.infoContainer}>
          <Text variant="body2" color="secondary" align="center">
            You can also email us directly at {SUPPORT_EMAIL}
          </Text>
          <View style={styles.gap} />
          <Button title="Copy support email" variant="secondary" onPress={handleCopyEmail} />
        </View>
      </ContentContainer>

      <BottomContainer>
        <Button
          title={isSubmitting ? 'Sending...' : 'Send'}
          onPress={handleSubmit}
          disabled={!canSubmit}
        />
      </BottomContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.md,
    backgroundColor: Theme.colors.surface,
    borderBottomWidth: 0.5,
    borderBottomColor: Theme.colors.border,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  placeholder: {
    width: 40,
  },
  card: {
    padding: Theme.spacing.xl,
  },
  gap: {
    height: Theme.spacing.md,
  },
  sectionLabel: {
    marginBottom: Theme.spacing.sm,
  },
  fieldLabel: {
    marginBottom: Theme.spacing.xs,
    fontWeight: '500',
  },
  emailHint: {
    marginTop: 2,
    fontStyle: 'italic',
  },
  divider: {
    height: 1,
    backgroundColor: Theme.colors.border,
    marginVertical: Theme.spacing.lg,
  },
  dropdown: {
    borderRadius: Theme.borderRadius.md,
    borderWidth: 1,
    borderColor: Theme.colors.border,
    backgroundColor: Theme.colors.surface,
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.md,
  },
  dropdownText: {
    color: Theme.colors.textPrimary,
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  modalCenter: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCard: {
    padding: Theme.spacing.md,
    width: '86%',
  },
  optionRow: {
    paddingVertical: Theme.spacing.md,
  },
  optionRowSelected: {
    backgroundColor: Theme.colors.primaryLight,
    borderRadius: Theme.borderRadius.sm,
    paddingHorizontal: Theme.spacing.sm,
  },
  infoContainer: {
    marginTop: Theme.spacing.lg,
    padding: Theme.spacing.lg,
    alignItems: 'center',
  },
});

export default ContactUsScreen;


